import React, {useEffect, Suspense} from 'react'
import { observer } from "mobx-react-lite"
import { Router, globalHistory } from "@reach/router"
import PrivateRoute from '../features/common/PrivateRoute'
import useUser from '../features/common/useUser'
import InitApp from '../components/InitApp'
import RouteLoader from '../components/RouteLoader'
import SubscribeModal from '../features/account/SubscribeModal'

// Prerendered via react-snapshot (reactSnapshot.include in package.json), which uses
// ReactDOMServer.renderToString under the hood — React 17's renderToString cannot render
// a pending Suspense/lazy tree (throws "ReactDOMServer does not yet support Suspense").
// These routes must stay statically imported.
import NotFound from '../features/layout/NotFound'
import Index from '../features/index'
import LivePerformance from '../features/catalog/LivePerformance'
import ProducerSounds from '../features/catalog/ProducerSounds'
import TrainModel from '../features/welcome/TrainModel'
import Faq from '../features/help/faq'
import DrumNet from '../features/welcome/DrumNet'
import VocalNet from '../features/welcome/VocalNet'
import VocalNetZh from '../features/welcome/VocalNetZh'
import VocalNetTutorial from '../features/welcome/VocalNetTutorial'
import VocalNetCommunity from '../features/welcome/VocalNetCommunity'
import PitchNet from '../features/welcome/PitchNet'
// import DrumNetZh from '../features/welcome/DrumNetZh'
import Packs from '../features/welcome/Packs'
import Reviews from '../features/welcome/Reviews'
import ShortcutsAndFx from '../features/help/shortcuts-and-fx'
import Copyrights from '../features/policies/Copyrights'
import Google from '../features/policies/Google'
import PrivacyPolicy from '../features/policies/PrivacyPolicy'
import PricingTerms from '../features/policies/PricingTerms'
import AcceptableUseAndFairUsagePolicy from '../features/policies/AcceptableUseAndFairUsagePolicy'
import TermsOfService from '../features/policies/TermsOfService'
import Pricing from '../features/account/Pricing'
import Promo from '../features/promo/Promo'

// Never touched by the prerender crawler (reactSnapshot.exclude), safe to code-split.
// Each gets its OWN Suspense boundary (via withSuspense, or inside PrivateRoute for the
// account-gated ones) rather than one shared boundary around the whole Router — a Suspense
// element present anywhere in the tree breaks renderToString for the *other*, eager routes
// too, even when nothing under it is actually pending, so it must never wrap an eager page.
const Pack = React.lazy(() => import('../features/pack/Pack'))
const MyLibrary = React.lazy(() => import('../features/my/MyLibrary'))
const Auth = React.lazy(() => import('../features/auth/Auth'))
const Settings = React.lazy(() => import('../features/account/Settings'))
const VerifyEmail = React.lazy(() => import('../features/account/VerifyEmail'))
const ResetPassword = React.lazy(() => import('../features/auth/ResetPassword'))
const PayPlan = React.lazy(() => import('../features/account/PayPlan'))
const Checkout = React.lazy(() => import('../features/account/Checkout'))
const Share = React.lazy(() => import('../features/share/Share'))
const Unsubscribe = React.lazy(() => import('../features/auth/Unsubscribe'))
const AppBuy = React.lazy(() => import('../features/account/AppBuy'))
const FileUpload = React.lazy(() => import('../features/upload/FileUpload'))

const withSuspense = (Comp) => (props) => (
  <Suspense fallback={<RouteLoader />}><Comp {...props} /></Suspense>
)

const PackRoute = withSuspense(Pack)
const AuthRoute = withSuspense(Auth)
const ResetPasswordRoute = withSuspense(ResetPassword)
const VerifyEmailRoute = withSuspense(VerifyEmail)
const UnsubscribeRoute = withSuspense(Unsubscribe)
const ShareRoute = withSuspense(Share)

// 路由
const Routes = observer(() => {
  const { user, init } = useUser()
  useEffect(() => {
    init()
  }, [init, user.auth])

  useEffect(() => {
    const firePageView = (location) => {
      window.gtag && window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_title: document.title
      })
    }
    firePageView(globalHistory.location)
    return globalHistory.listen(({ location }) => firePageView(location))
  }, [])

  if (user.auth === 0) {
    return <InitApp />
  }

  return (
    <>
    <SubscribeModal />
    <Router>
      <Index path="/" />
      <LivePerformance path="/catalog/live-performance" />
      <ProducerSounds path="/catalog/producer-sounds" />
      <TrainModel path="/train-model" />
      <VocalNet path="/vocalnet" />
      <VocalNetZh path="/vocalnet/zh" />
      <VocalNetCommunity path="/vocalnet-community" />
      <VocalNetTutorial path="/vocalnet-tutorial" />
      <PitchNet path="/pitchnet" />
      <DrumNet path="/drumnet" />
      {/*<DrumNetZh path="/drumnet/zh" />*/}
      <Packs path="/packs" />
      <PrivateRoute as={Settings} path="/account/*" />
      <PrivateRoute as={PayPlan} path="/account/pay-plan" />
      <PrivateRoute as={Checkout} path="/account/checkout" />
      <PrivateRoute as={AppBuy} path="/account/app-buy" />
      <PrivateRoute as={FileUpload} path="/upload" />
      <Pricing path="/pricing" />
      <Promo path="/promo" />
      {/* <PrivateRoute as={Home} path="/" /> */}
      <PackRoute path="/pack/:packId" />
      <PrivateRoute as={MyLibrary} path="/my-library" />

      <ResetPasswordRoute path="/auth/reset-password" />
      <VerifyEmailRoute path="/auth/verify-email" />
      <VerifyEmailRoute path="/auth/change-email" />
      <UnsubscribeRoute path="/auth/unsubscribe" />
      <AuthRoute path="/auth/*" />

      <TermsOfService path="/policies/terms-of-service" />
      <AcceptableUseAndFairUsagePolicy path="/policies/acceptable-use-and-fair-usage-policy" />
      <Copyrights path="/policies/copyrights" />
      <PricingTerms path="/policies/pricing-terms" />
      <PrivacyPolicy path="/policies/privacy-policy" />
      <Google path="/policies/google" />

      <Faq path="/help/faq" />
      <Reviews path="/reviews" />
      <ShortcutsAndFx path="/help/shortcuts-and-fx" />

      <ShareRoute path="/s/:shareCode" />
      <ShareRoute path="/i/:shareCode" />

      <NotFound path="/404" />
      <NotFound path="*" />
    </Router>
    </>
  )
})

export default Routes
